/* Elysian brew calculator, the flip card (round 10, 2026-10-09). sections/elysian-brew-calculator-flip.liquid,
   assets/elysian-brew-calculator-flip.css. Built from the one-screen version (assets/elysian-brew-calculator-one.js, round 9): one card,
   everything you choose and the live recipe on its front; Start turns it over (3D, in place, the page never scrolls) to the timer on
   its back. Edit Recipe turns it back with the brew paused; Enjoy and the taste check when the brew ends; Brew Again turns it back.
   The model below (ratios, limits, picks' maths, cups, the timer programmes) is the one-screen script's model block, byte for byte
   (lines 11 to 188 of both files diff empty), so every number matches. Only the interface after it is new.
   Plain ES5; every newer API (Wake Lock, vibration, matchMedia, inert, focus options) is feature-detected or harmless when missing. */
(function () {
  'use strict';

  /* ---------- The model ---------- */

  // One fixed brew ratio (water : coffee) per brewer, set by Chris (round 2; round 5 for the Auto Brewer, Elysian's 1:16 kept even though
  // batch sources run from about 1:14 to 1:18).
  //   held     water the grounds hold back from the cup, used for the Brewed Coffee direction and the "makes about" line.
  //            Pour-over and auto brewer 2.1 g/g (UC Davis [S1]; Workshop Coffee's Moccamaster guide: 1 L of water gives about 850 ml,
  //            which is 2.1 to 2.4 g/g at 60 to 65 g). French press 2.5 g/g: Liang 2021 measured 2.48 for filtered immersion [S2]; no
  //            primary measurement exists for a pressed French press, so this is an estimate. AeroPress 2.0 g/g: an ESTIMATE, no primary
  //            measurement found; the press squeezes the puck fairly dry, so it is set below the French press.
  //   start    the amount each direction resets to when this brewer is chosen (round 3), in grams.
  //   cap      the most water the brewer holds, in grams; the largest dose is the biggest half gram whose water fits.
  //            AeroPress: the Original's chamber is 10 oz (296 ml) in all and the coffee and plunger take room, so about 250 g
  //            (16 g at 1:15.5). Auto Brewer: a Moccamaster carafe is 1.25 L (manual: 10 cups of 4 oz; 75 g for a full brew), so
  //            1,250 g (78 g at 1:16). A Bonavita 8-cup holds a little less. French press: a large 12-cup press is about 1.5 L in all,
  //            and the grounds and crust take room, so 1,350 g (90 g at 1:15).
  //   maxDose  a limit set in grams of coffee rather than by capacity. Pour-Over: 40 g (640 g of water), the top of a 1 to 4 cup cone
  //            such as the V60-02; above that the Auto Brewer is the right tool.
  //   minDose  Auto Brewer only, 40 g (Chris: nothing under about 40 g). At 1:16 that is 640 g of water, about half a Moccamaster
  //            carafe, where the manual already switches the drip-stop to half-open for small amounts.
  //   guide    the brewer has no timer, only the How to Brew list (Auto Brewer).
  var METHODS = {
    pourover: { name: 'Pour-Over', ratio: 16, held: 2.1, grind: 'medium-fine', start: { have: 20, want: 240 }, maxDose: 40 },
    autobrewer: { name: 'Auto Brewer', ratio: 16, held: 2.1, grind: 'medium', start: { have: 60, want: 720 }, cap: 1250, minDose: 40, guide: true },
    aeropress: { name: 'AeroPress', ratio: 15.5, held: 2.0, grind: 'fine', start: { have: 14, want: 200 }, cap: 250 },
    frenchpress: { name: 'French Press', ratio: 15, held: 2.5, grind: 'coarse', start: { have: 30, want: 480 }, cap: 1350 }
  };

  // Auto Brewer: the half-batch line in How to Brew shows up to 750 g of water (Workshop Coffee's Moccamaster KBG Select guide:
  // half batch for anything up to 750 ml and 45 g; the Moccamaster manual: drip-stop half-open for small amounts).
  var HALF_BATCH = 750;

  // Pour-over finish, by dose (round 5, Chris: timings follow the research). Hoffmann's 1 cup V60 (15 g) drains by about 3:00; his
  // Ultimate V60 (30 g) and Kasuya's 4:6 (20 g) by about 3:30; Rao's 20 g runs 4:00 to 4:30, and a deeper bed drains slower, so
  // above 30 g the target is 4:00. The step times themselves are unchanged.
  function pourFinish(dose) {
    if (!isFinite(dose)) return 210;
    return dose <= 15 ? 180 : dose <= 30 ? 210 : 240;
  }

  // AeroPress steep, fixed (round 3): a 1:30 countdown, then press for about 30 seconds. Published recipes press between 0:30 and
  // 2:00 (AeroPress Inc. about 0:30 to 1:00, Wendelboe 1:00, the 2023 world champion 1:35, Hoffmann 2:00); 1:30 sits in the
  // middle and inside Chris's "much shorter" direction. Sources in BUILD-REPORT.md, round 3.
  var AERO_STEEP = 90;

  // A cup of coffee (round 6). Chris, Oct 8: "let's actually change our 'cups' back to 8 oz I think for everything. Redo all the
  // math." 8 oz is 236.6 g; the Brewed Coffee quick picks use 240 g per cup so the maths stays clean (1, 2, 3 and 4 cups are 240,
  // 480, 720 and 960 g). The "makes about" line converts grams to ounces at 29.57 g.
  var GRAMS_PER_OZ = 29.57;
  var OZ_PER_CUP = 8;
  var GRAMS_PER_CUP = 240;

  // Beans in grams (arrow keys 1 g, Shift 10 g); Brewed Coffee in grams (arrow keys 10 g, Shift 100 g).
  var LIMITS = { have: { min: 1, max: 500, step: 1, big: 10, start: 20 }, want: { min: 30, max: 3000, step: 10, big: 100, start: 300 } };

  // The largest amount a brewer allows in a direction. Beans: maxDose, or the biggest half gram whose water fits the cap
  // (AeroPress 250 / 15.5 = 16.1, so 16 g and 248 g). Brewed Coffee: the biggest 10 g step whose rounded dose is still within it
  // (AeroPress: the dose rounds to 16 below 16.25 x 13.5 = 219.4 g, so 210 g, which gives 15.5 g and 240 g).
  function doseMax(m) { return m.maxDose || (m.cap ? Math.floor(m.cap / m.ratio * 2) / 2 : 0); }
  function maxFor(m, dir) {
    var dose = doseMax(m);
    if (!dose) return LIMITS[dir].max;
    if (dir === 'have') return dose;
    return Math.floor(((dose + 0.25) * (m.ratio - m.held) - 0.001) / 10) * 10;
  }
  // The smallest: Beans minDose; Brewed Coffee the smallest 10 g step whose rounded dose reaches it (Auto Brewer: 39.75 x 13.9 =
  // 552.5 g, so 560 g, which gives 40.5 g and 648 g).
  function minFor(m, dir) {
    if (!m.minDose) return LIMITS[dir].min;
    if (dir === 'have') return m.minDose;
    return Math.ceil(((m.minDose - 0.25) * (m.ratio - m.held) + 0.001) / 10) * 10;
  }

  function roundHalf(x) { return Math.round(x * 2) / 2; }
  function fmtGrams(x) { return (Math.round(x * 10) / 10).toString(); }
  // display text only (never the field, where a comma would read as a decimal): 1280 -> "1,280"
  function group(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  function fmtRatio(r) { return '1:' + (Math.round(r * 10) / 10).toString(); }
  function fmtTime(sec) {
    sec = Math.max(0, Math.floor(sec));
    var m = Math.floor(sec / 60), s = sec % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }
  function parseAmount(str) {
    str = String(str).replace(/\s/g, '');
    // "1,200" is a thousands separator; "18,5" is a decimal comma
    if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(str)) str = str.replace(/,/g, '');
    var v = parseFloat(str.replace(',', '.').replace(/[^0-9.]/g, ''));
    return isFinite(v) ? v : NaN;
  }
  function setText(el, txt) { if (el && el.textContent !== txt) el.textContent = txt; }

  // "Makes about 9 oz, or 1 cup of coffee." Ounces to the nearest whole; cups = ounces / 8 to the nearest half.
  function makesAbout(grams) {
    var oz = Math.max(1, Math.round(grams / GRAMS_PER_OZ));
    var cups = Math.round(oz / OZ_PER_CUP * 2) / 2;
    if (cups < 0.5) return 'Makes about ' + oz + ' oz of coffee.';
    var whole = Math.floor(cups), half = cups - whole > 0;
    var word = (whole === 0 ? '' : String(whole)) + (half ? '\u00bd' : '');
    return 'Makes about ' + oz + ' oz, or ' + word + (cups > 1 ? ' cups' : ' cup') + ' of coffee.';
  }

  // Round 4: the quiet equivalent beside a Brewed Coffee amount. Pour-over, auto brewer and French press count cups of 240 g (the
  // quick picks' cup, round 6), to the nearest half: "about 4 cups". The AeroPress, and anything under half a cup, uses ounces:
  // "about 7 oz". The caller puts "of liquid coffee · " in front (round 6, Chris: the Brewed Coffee tab always means liquid coffee).
  function aboutCups(grams, method) {
    if (!isFinite(grams) || grams <= 0) return '';
    if (method === 'aeropress') return 'about ' + Math.max(1, Math.round(grams / GRAMS_PER_OZ)) + ' oz';
    var cups = Math.round(grams / GRAMS_PER_CUP * 2) / 2;
    if (cups < 0.5) return 'about ' + Math.max(1, Math.round(grams / GRAMS_PER_OZ)) + ' oz';
    var whole = Math.floor(cups), half = cups - whole > 0;
    return 'about ' + (whole === 0 ? '' : String(whole)) + (half ? '\u00bd' : '') + (cups > 1 ? ' cups' : ' cup');
  }

  // The recipe for the current state. In the Brewed Coffee direction the dose is rounded to the half gram, and the water is computed
  // from that rounded dose, so the recipe card's three numbers agree (coffee x ratio = water, to the gram).
  function recipe(st) {
    var m = METHODS[st.method];
    var r = m.ratio;
    var dose;
    if (st.dir === 'have') {
      dose = st.have;
    } else {
      dose = isFinite(st.want) && st.want > 0 ? roundHalf(st.want / (r - m.held)) : NaN;
    }
    var ok = isFinite(dose) && dose > 0;
    var water = ok ? Math.round(dose * r) : NaN;
    var cup = ok ? water - m.held * dose : NaN;
    return { m: m, ratio: r, dose: dose, water: water, cup: cup, ok: ok };
  }

  /* ---------- Timer programmes ---------- */

  // Pour-over counts up: bloom with 3x the dose to 0:40 (round 7, Chris, Oct 8: "3x for pour over is what I do"; Rao blooms 20 g
  // with 60 g, 3x, research-2026-10-08.md), with a gentle stir of the slurry so all the coffee gets wet. Then pour to 60% of the
  // water by 1:15, to 100% by 2:00, draw down, finish by pourFinish(dose): 3:00, 3:30 or 4:00 (round 5). French press counts down 6:00: a 4:00 steep, then a 2:00 settle
  // (round 6; Wendelboe waits 1 to 5 minutes after breaking the crust, Hoffmann 5 to 8; research-2026-10-08.md). AeroPress counts down a fixed 1:30
  // steep (AERO_STEEP), then the press. The Auto Brewer has no programme: it shows How to Brew instead.
  // Pour-over bloom water, as a multiple of the dose (round 7: 3x; it was 2x through round 6).
  var BLOOM = 3;
  function programme(st, rec) {
    var w = rec.ok ? rec.water : null;
    var bloom = rec.ok ? Math.round(rec.dose * BLOOM) : null;
    var sixty = rec.ok ? Math.round(rec.water * 0.6) : null;
    if (st.method === 'pourover') {
      var fin = pourFinish(rec.ok ? rec.dose : NaN);
      return {
        mode: 'up', total: fin, stop: 600, meta: 'Pour-Over · About ' + fmtTime(fin),
        steps: [
          { at: 0, name: 'Bloom', verb: 'Pour to', num: bloom, unit: ' g', hint: 'Pour, then gently stir the slurry so all the coffee gets wet. Wait.', say: 'pour to' },
          { at: 40, name: 'First Pour', verb: 'Pour to', num: sixty, unit: ' g', hint: 'Pour slowly, in small circles.', say: 'pour to' },
          { at: 75, name: 'Second Pour', verb: 'Pour to', num: w, unit: ' g', hint: 'Keep the level steady as you go.', say: 'pour to' },
          { at: 120, name: 'Draw Down', verb: 'Finish by about', num: fmtTime(fin), unit: '', hint: 'Let it drain through. Aim to finish between ' + fmtTime(fin - 30) + ' and ' + fmtTime(fin) + '.', say: 'finish by about' }
        ],
        late: { at: fin, hint: 'Still draining? Grind a little coarser next time.' }
      };
    }
    if (st.method === 'frenchpress') {
      return {
        mode: 'down', total: 360, meta: 'French Press · 4:00 Steep, 2:00 Settle',
        steps: [
          { at: 0, name: 'Pour, Stir and Lid', verb: 'Pour', num: w, unit: ' g', hint: 'Pour all the water, stir gently, then put the lid on.', say: 'pour' },
          { at: 240, name: 'Break the Crust and Skim', verb: '', num: null, unit: '', hint: 'Then leave it to settle.' },
          { at: 360, name: 'Press Gently and Pour', verb: '', num: null, unit: '', hint: 'Press just to the surface and pour slowly.' }
        ]
      };
    }
    return {
      mode: 'down', total: AERO_STEEP, meta: 'AeroPress · ' + fmtTime(AERO_STEEP) + ' Steep',
      steps: [
        { at: 0, name: 'Pour and Stir', verb: 'Pour', num: w, unit: ' g', hint: 'Add all the water, stir, then set the plunger on top.', say: 'pour' },
        { at: AERO_STEEP, name: 'Press for About 30 Seconds', verb: '', num: null, unit: '', hint: 'Press slowly and stop when you hear a hiss.' }
      ]
    };
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---------- Round 10: the flip card ---------- */

  // The brewing family, on the front face's meta line next to the time (round 9).
  var FAMILY = { pourover: 'Percolation', autobrewer: 'Automatic Drip', aeropress: 'Hybrid', frenchpress: 'Immersion' };

  // The turn: 520 ms in the CSS (--bf-flip). The face turned away is hidden (visibility) once it ends.
  var FLIP = 520;
  // the card is edge on at 20 percent of the turn on cubic-bezier(0.2, 0, 0, 1): the faces swap there (CSS --bf-half)
  var HALF = 104;
  // A pour-over keeps counting past its finish time; Enjoy and the taste check come 30 s after it (round 9, review S7).
  var TASTE_AFTER = 30;

  function init(root) {
    if (root.getAttribute('data-ely-bf-ready')) return;
    root.setAttribute('data-ely-bf-ready', '1');
    var q = function (sel) { return root.querySelector(sel); };
    var qa = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

    var st = { method: 'pourover', dir: 'have', have: LIMITS.have.start, want: LIMITS.want.start, over: false, under: false };
    var app = q('[data-bf-app]');
    var LABELS = { have: (app && app.getAttribute('data-label-have')) || 'Beans', want: (app && app.getAttribute('data-label-want')) || 'Brewed Coffee' };

    var el = {
      methods: qa('.ely-bf__method input'),
      dirs: qa('.ely-bf__dir input'),
      amount: q('[data-amount]'),
      amountLabel: q('[data-amount-label]'),
      chipSets: qa('[data-chips]'),
      chips: qa('[data-chip]'),
      caps: qa('[data-cap]'),
      equiv: q('[data-equiv]'),
      card: q('[data-card]'),
      front: q('[data-face="front"]'),
      back: q('[data-face="back"]'),
      idleMeta: q('[data-idle-meta]'),
      nums: q('[data-nums]'),
      dose: q('[data-out="dose"] [data-num]'),
      water: q('[data-out="water"] [data-num]'),
      ratio: q('[data-out="ratio"] [data-num]'),
      cup: q('[data-out="yield"]'),
      grind: q('[data-grind]'),
      summary: q('[data-summary]'),
      go: q('[data-go]'),
      over: q('[data-over]'),
      frontActions: q('.ely-bf__front-actions'),
      backSummary: q('[data-back-summary]'),
      backTitle: q('[data-back-title]'),
      timer: q('[data-timer]'),
      done: q('[data-done-face]'),
      last: q('[data-last]'),
      guide: q('[data-guide]'),
      gDose: q('[data-guide-dose]'),
      gWater: q('[data-guide-water]'),
      gMl: q('[data-guide-ml]'),
      gHalf: q('[data-guide-half]'),
      clock: q('[data-clock]'),
      now: q('[data-now]'),
      nowName: q('[data-now-name]'),
      nowTarget: q('[data-now-target]'),
      nowVerb: q('[data-now-verb]'),
      nowNum: q('[data-now-num]'),
      nowUnit: q('[data-now-unit]'),
      nowHint: q('[data-now-hint]'),
      barWrap: q('[data-bar-wrap]'),
      bar: q('[data-bar]'),
      next: q('[data-next]'),
      plan: q('[data-plan]'),
      buzz: q('[data-buzz]'),
      buzzInput: q('[data-buzz-input]'),
      sound: q('[data-sound]'),
      soundInput: q('[data-sound-input]'),
      backGo: q('[data-back-go]'),
      edit: q('[data-edit]'),
      backActions: q('.ely-bf__back-actions'),
      announce: q('[data-announce]'),
      tasteOpts: qa('[data-taste-opt]'),
      tasteTip: q('[data-taste-tip]')
    };

    // Read the form as the browser restored it (back/forward cache, autofill), so the numbers match what is on screen.
    el.methods.forEach(function (i) { if (i.checked) st.method = i.value; });
    el.dirs.forEach(function (i) { if (i.checked) st.dir = i.value; });
    st.have = METHODS[st.method].start.have;
    st.want = METHODS[st.method].start.want;
    var restored = parseAmount(el.amount.value);
    if (isFinite(restored) && restored > 0) st[st.dir] = Math.max(minFor(METHODS[st.method], st.dir), Math.min(restored, maxFor(METHODS[st.method], st.dir)));

    // the amount the recipe uses: what was typed, held inside the brewer's limits (the field keeps the typed number until blur)
    function amountFor(dir) {
      var m = METHODS[st.method], v = st[dir], mx = maxFor(m, dir), mn = minFor(m, dir);
      return isFinite(v) && v > mx ? mx : isFinite(v) && v < mn ? mn : v;
    }
    var underLate = false, underTimer = null;
    function chipKey() { return st.method + '-' + st.dir; }
    function current() { return recipe({ method: st.method, dir: st.dir, have: amountFor('have'), want: amountFor('want') }); }
    function guided() { return !!METHODS[st.method].guide; }
    function say(txt) {
      setText(el.announce, '');
      setTimeout(function () { setText(el.announce, txt); }, 50);
    }

    /* ----- the turn ----- */
    // side: 'front' (the inputs and the recipe) or 'back' (the timer, Enjoy, How to Brew). Both faces share one grid cell, so the card
    // is the height of the taller face whichever way it faces. The face turned away is inert and aria-hidden at once, and
    // visibility: hidden once the turn ends. The page never scrolls: focus moves with preventScroll, and if anything moved the page
    // anyway it is put back on the same frame.
    var side = 'front', turnTimer = null, focusTimer = null;
    function setInert(n, on) {
      if (on) { n.setAttribute('inert', ''); n.setAttribute('aria-hidden', 'true'); }
      else { n.removeAttribute('inert'); n.removeAttribute('aria-hidden'); }
    }
    function focusQuiet(n) {
      if (!n) return;
      try { n.focus({ preventScroll: true }); } catch (err) { n.focus(); }
    }
    el.back.hidden = false;
    el.back.classList.add('is-away');
    setInert(el.back, true);

    function showNow(n, on) {
      n.style.transition = on ? 'none' : '';
      n.style.visibility = on ? 'visible' : '';
    }

    function turn(to, focusEl) {
      if (to === side) { focusQuiet(focusEl); return; }
      side = to;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var on = to === 'back' ? el.back : el.front, off = to === 'back' ? el.front : el.back;
      var card = el.card;
      clearTimeout(turnTimer);
      showNow(el.front, false);
      showNow(el.back, false);
      setInert(on, false);
      setInert(off, true);
      card.setAttribute('data-side', to);
      var settle = function () {
        showNow(on, false);
        showNow(off, false);
        if (side !== to) return;
        off.classList.add('is-away');
        card.classList.remove('is-3d', 'is-held');
      };
      if (reducedMotion()) {
        // no rotation: the faces swap where they stand
        card.classList.remove('is-3d', 'is-held');
        card.classList.toggle('is-flipped', to === 'back');
        on.classList.remove('is-away');
        settle();
      } else if (card.classList.contains('is-3d')) {
        // turned again mid-turn: the same transition simply reverses from where it is
        on.classList.remove('is-away');
        off.classList.add('is-away');
        card.classList.toggle('is-flipped', to === 'back');
        turnTimer = setTimeout(settle, FLIP + 40);
      } else {
        // put the card into 3D at the side it rests on, with no transition, then turn it. The faces' visibility swaps with a
        // 104 ms delay (CSS), the moment the card is edge on, so neither face is ever seen from behind.
        card.classList.add('is-held', 'is-3d');
        card.classList.toggle('is-flipped', to !== 'back');
        void card.offsetWidth;
        card.classList.remove('is-held');
        on.classList.remove('is-away');
        off.classList.add('is-away');
        void card.offsetWidth;
        card.classList.toggle('is-flipped', to === 'back');
        turnTimer = setTimeout(settle, FLIP + 40);
      }
      // focus once the face is visible (a hidden element cannot take focus): at once without motion, at the edge-on moment otherwise
      var keep = function () {
        if (side !== to) return;
        // past the edge-on moment this face is showing; say so inline as well (with no transition), so focus never races the CSS
        // transition's own clock, which starts on the next style update rather than at the tap
        showNow(on, true);
        focusQuiet(focusEl);
        var y1 = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (Math.abs(y1 - y) > 0.5) window.scrollTo(0, y);
      };
      clearTimeout(focusTimer);
      if (reducedMotion()) keep(); else focusTimer = setTimeout(keep, HALF + 16);
    }

    /* ----- calculator rendering ----- */
    var summaryTimer = null;
    function recipeKey(rec) { return st.method + ':' + (rec.ok ? rec.dose + ':' + rec.water : 'none'); }
    function render(announce) {
      var rec = current();
      var prog = rec.m.guide ? null : programme(st, rec);
      // a paused brew belongs to the recipe it started with; a new brewer or amount resets it (Start returns)
      if (tm.state === 'paused' && tm.key !== recipeKey(rec)) resetTimer();
      // the limit lines: shown while the typed amount is past a limit, and after it was pulled back to the limit on blur
      var mx = maxFor(rec.m, st.dir), mn = minFor(rec.m, st.dir), tv = st[st.dir];
      var line = '';
      if (isFinite(tv) && (tv > mx || (st.over && tv === mx))) line = st.method + '-max';
      else if (isFinite(tv) && ((tv < mn && underLate) || (st.under && tv === mn))) line = st.method + '-min';
      el.caps.forEach(function (c) { c.hidden = c.getAttribute('data-cap') !== line; });
      setText(el.dose, rec.ok ? fmtGrams(rec.dose) : '–');
      setText(el.water, rec.ok ? group(rec.water) : '–');
      setText(el.ratio, fmtRatio(rec.ratio));
      var long = rec.ok && (fmtGrams(rec.dose).length + group(rec.water).length) >= 8;
      el.nums.classList.toggle('is-long', long);
      // the meta line: brewer, family, time, held to one line
      setText(el.idleMeta, rec.m.name + ' · ' + FAMILY[st.method] + ' · ' + (prog ? 'About ' + fmtTime(prog.total) : 'About 4 to 6 Minutes'));
      // the "makes about" line belongs to the Beans direction only; in Brewed Coffee the amount already is the cup
      el.cup.hidden = st.dir !== 'have';
      setText(el.cup, rec.ok ? makesAbout(rec.cup) : 'Enter an amount in grams.');
      setText(el.grind, 'Start ' + rec.m.grind);
      // the back face's one-line summary: "Pour-Over · 20 g · 320 g · 1:16"
      setText(el.backSummary, rec.m.name + ' · ' + (rec.ok ? fmtGrams(rec.dose) + ' g · ' + group(rec.water) + ' g · ' : '') + fmtRatio(rec.ratio));
      if (rec.m.guide) {
        setText(el.gDose, rec.ok ? fmtGrams(rec.dose) + ' g' : '–');
        setText(el.gWater, rec.ok ? group(rec.water) + ' g' : '–');
        setText(el.gMl, rec.ok ? group(rec.water) + ' ml' : '–');
        el.gHalf.hidden = !(rec.ok && rec.water <= HALF_BATCH);
      }
      // Brewed Coffee only: "of liquid coffee · about 2 cups", inside the field (the amount typed, held under any cap)
      var eq = st.dir === 'want' ? aboutCups(amountFor('want'), st.method) : '';
      el.equiv.hidden = !eq;
      setText(el.equiv, eq ? 'of liquid coffee · ' + eq : '');
      // the quick pick that matches the amount shows as chosen
      var v = st[st.dir], key = chipKey();
      el.chips.forEach(function (c) {
        var on = c.parentNode.getAttribute('data-chips') === key && isFinite(v) && parseFloat(c.getAttribute('data-chip')) === v;
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (announce && el.summary) {
        clearTimeout(summaryTimer);
        summaryTimer = setTimeout(function () {
          setText(el.summary, rec.ok ? 'Coffee ' + fmtGrams(rec.dose) + ' grams, water ' + rec.water + ' grams, ratio 1 to ' + (Math.round(rec.ratio * 10) / 10) + '.' : '');
        }, 700);
      }
      renderTimer(rec);
    }

    // the field grows with its number, so the unit always sits right beside it
    function fitField() {
      var n = Math.max(1, el.amount.value.length);
      el.amount.style.width = (n + 0.1) + 'ch';
    }
    function syncAmountField() {
      var v = st[st.dir];
      el.amount.value = isFinite(v) ? fmtGrams(v) : '';
      fitField();
      setText(el.amountLabel, LABELS[st.dir]);
      var key = chipKey();
      el.chipSets.forEach(function (set) { set.hidden = set.getAttribute('data-chips') !== key; });
    }
    function stepAmount(dir, big) {
      var lim = LIMITS[st.dir];
      var v = st[st.dir];
      if (!isFinite(v)) v = METHODS[st.method].start[st.dir];
      var mx = maxFor(METHODS[st.method], st.dir), mn = minFor(METHODS[st.method], st.dir);
      var want = v + dir * (big ? lim.big : lim.step);
      var nv = Math.min(mx, Math.max(mn, Math.round(want * 10) / 10));
      st.over = dir > 0 && nv === mx && want > mx;
      st.under = dir < 0 && nv === mn && want < mn;
      st[st.dir] = nv;
      syncAmountField();
      el.amount.select();
      render(true);
    }

    // a new brewer resets the amount to its default (and a paused brew, through render)
    el.methods.forEach(function (i) {
      i.addEventListener('change', function () {
        if (!i.checked) return;
        st.method = i.value;
        st.have = METHODS[st.method].start.have;
        st.want = METHODS[st.method].start.want;
        st.over = false; st.under = false; underLate = false;
        syncAmountField();
        render(true);
      });
    });
    el.dirs.forEach(function (i) {
      i.addEventListener('change', function () {
        if (!i.checked) return;
        st.dir = i.value;
        st.over = false; st.under = false; underLate = false;
        syncAmountField();
        render(true);
      });
    });
    el.amount.addEventListener('input', function () {
      var v = parseAmount(el.amount.value);
      var lim = LIMITS[st.dir];
      st[st.dir] = isFinite(v) && v >= lim.min && v <= lim.max ? v : NaN;
      st.over = false; st.under = false; underLate = false;
      clearTimeout(underTimer);
      if (isFinite(st[st.dir]) && st[st.dir] < minFor(METHODS[st.method], st.dir)) {
        underTimer = setTimeout(function () { underLate = true; render(false); }, 900);
      }
      fitField();
      render(true);
    });
    el.amount.addEventListener('blur', function () {
      var lim = LIMITS[st.dir];
      var v = parseAmount(el.amount.value);
      if (!isFinite(v)) v = METHODS[st.method].start[st.dir];
      var mx = maxFor(METHODS[st.method], st.dir), mn = minFor(METHODS[st.method], st.dir);
      clearTimeout(underTimer); underLate = false;
      v = Math.max(lim.min, Math.round(v * 10) / 10);
      if (v > mx) { st.over = true; v = mx; }
      if (v < mn) { st.under = true; v = mn; }
      st[st.dir] = Math.min(lim.max, v);
      syncAmountField();
      render(true);
    });
    // select the whole number on focus, so typing replaces it (Safari drops a select() made during focus, hence the mouseup guard)
    var keepSelection = false;
    el.amount.addEventListener('focus', function () {
      keepSelection = true;
      setTimeout(function () { try { el.amount.select(); } catch (err) {} }, 0);
    });
    el.amount.addEventListener('mouseup', function (ev) {
      if (keepSelection) { ev.preventDefault(); keepSelection = false; }
    });
    el.amount.addEventListener('keydown', function (ev) {
      keepSelection = false;
      if (ev.key === 'ArrowUp' || ev.key === 'Up') { ev.preventDefault(); stepAmount(1, ev.shiftKey); }
      else if (ev.key === 'ArrowDown' || ev.key === 'Down') { ev.preventDefault(); stepAmount(-1, ev.shiftKey); }
      else if (ev.key === 'Enter') { el.amount.blur(); }
    });
    el.chips.forEach(function (c) {
      c.addEventListener('click', function () {
        st[st.dir] = parseFloat(c.getAttribute('data-chip'));
        st.over = false; st.under = false; underLate = false; clearTimeout(underTimer);
        syncAmountField();
        render(true);
      });
    });

    /* ----- taste check: on the back face, after a brew ----- */
    var TASTE = {
      bitter: 'Grind a little coarser next time. Keep everything else the same.',
      right: 'Nice. Note your grind setting and brew it again.',
      watery: 'Grind a little finer next time. Keep everything else the same.'
    };
    el.tasteOpts.forEach(function (b) {
      b.addEventListener('click', function () {
        var k = b.getAttribute('data-taste-opt');
        el.tasteOpts.forEach(function (o) { o.setAttribute('aria-pressed', o === b ? 'true' : 'false'); });
        setText(el.tasteTip, TASTE[k]);
      });
    });
    function clearTaste() {
      el.tasteOpts.forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
      setText(el.tasteTip, '');
    }

    /* ----- timer ----- */
    var tm = { state: 'idle', acc: 0, since: 0, iv: null, idx: -1, late: false, key: '' };
    var lock = null;
    function elapsed() { return (tm.acc + (tm.state === 'running' ? Date.now() - tm.since : 0)) / 1000; }

    function lockOn() {
      if (!('wakeLock' in navigator) || !navigator.wakeLock || lock) return;
      try {
        navigator.wakeLock.request('screen').then(function (l) {
          lock = l;
          if (l && l.addEventListener) l.addEventListener('release', function () { lock = null; });
        }, function () {});
      } catch (err) { /* not allowed here: the timer still runs */ }
    }
    function lockOff() {
      if (lock) { try { lock.release(); } catch (err) {} lock = null; }
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && tm.state === 'running') { lockOn(); tick(); }
    });

    // Vibrate only where it can be felt: the API and a touch-first device with no hover (Android phones). Desktop Chrome has the API
    // without the hardware; iPhone Safari has neither.
    var mq = function (s) { return !!(window.matchMedia && window.matchMedia(s).matches); };
    var canBuzz = typeof navigator.vibrate === 'function' && mq('(pointer: coarse)') && !mq('(hover: hover)');
    if (canBuzz && el.buzz) el.buzz.hidden = false;

    // Sound: a soft two-note chime (660 then 880 Hz sine, about 220 ms, low volume) at each step and at the end, generated with Web
    // Audio, no files. Off until chosen; the choice is remembered. The audio context is made and resumed inside a tap (the toggle or
    // Start), which iOS requires. On an iPhone, Web Audio follows the ring / silent switch, so the chime is silent in silent mode.
    var SOUND_KEY = 'ely-brew-sound';
    var audio = null, chimes = 0;
    try { if (el.soundInput && window.localStorage.getItem(SOUND_KEY) === '1') el.soundInput.checked = true; } catch (err) {}
    function audioOn() {
      if (!el.soundInput || !el.soundInput.checked) return null;
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      try {
        if (!audio) audio = new AC();
        if (audio.state === 'suspended' && audio.resume) audio.resume();
      } catch (err) { audio = null; }
      return audio;
    }
    function chime() {
      if (!el.soundInput || !el.soundInput.checked) return;
      chimes++;
      root.setAttribute('data-chimes', String(chimes));
      var ac = audioOn();
      if (!ac) return;
      try {
        var t0 = ac.currentTime + 0.01;
        [[660, 0], [880, 0.11]].forEach(function (n) {
          var o = ac.createOscillator(), g = ac.createGain();
          o.type = 'sine';
          o.frequency.value = n[0];
          g.gain.setValueAtTime(0.0001, t0 + n[1]);
          g.gain.exponentialRampToValueAtTime(0.08, t0 + n[1] + 0.015);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + n[1] + 0.11);
          o.connect(g); g.connect(ac.destination);
          o.start(t0 + n[1]);
          o.stop(t0 + n[1] + 0.12);
        });
      } catch (err) { /* no sound here: the timer still runs */ }
    }
    if (el.soundInput) el.soundInput.addEventListener('change', function () {
      try { window.localStorage.setItem(SOUND_KEY, el.soundInput.checked ? '1' : '0'); } catch (err) {}
      audioOn();
    });

    // at each step the name, target and hint cross-fade with an 8 px rise over 160 ms (CSS); no halo, no scale
    function cue() {
      if (canBuzz && el.buzzInput && el.buzzInput.checked) { try { navigator.vibrate(120); } catch (err) {} }
      chime();
      if (!reducedMotion() && el.now) {
        el.now.classList.remove('is-step');
        void el.now.offsetWidth; // restart the animation
        el.now.classList.add('is-step');
      }
    }
    function stepIndex(prog, t) {
      var idx = 0;
      for (var i = 0; i < prog.steps.length; i++) if (t >= prog.steps[i].at) idx = i;
      return idx;
    }
    function stepWords(step) {
      var txt = step.name + '.';
      if (step.num !== null && step.num !== undefined && step.say) txt += ' ' + step.say.charAt(0).toUpperCase() + step.say.slice(1) + ' ' + step.num + (step.unit ? ' grams' : '') + '.';
      else if (step.hint) txt += ' ' + step.hint;
      return txt;
    }

    // the bar is the whole brew; a hairline tick marks the start of each step after the first (pour-over 0:40, 1:15, 2:00)
    var tickKey = '';
    function drawTicks(prog) {
      var key = prog.total + ':' + prog.steps.map(function (s) { return s.at; }).join(',');
      if (key === tickKey) return;
      tickKey = key;
      qa('.ely-bf__tick').forEach(function (n) { n.parentNode.removeChild(n); });
      prog.steps.forEach(function (s) {
        if (s.at <= 0 || s.at >= prog.total) return;
        var t = document.createElement('span');
        t.className = 'ely-bf__tick';
        t.style.left = (s.at / prog.total * 100).toFixed(3) + '%';
        el.barWrap.appendChild(t);
      });
    }

    // the step list: rebuilt when the programme changes, its classes moved on every tick
    var planKey = '';
    function drawPlan(prog, idx) {
      var key = prog.total + '|' + prog.steps.map(function (s) { return s.at + ':' + s.name + ':' + s.num; }).join('|');
      if (key !== planKey) {
        planKey = key;
        el.plan.innerHTML = '';
        prog.steps.forEach(function (s) {
          var li = document.createElement('li');
          var cells = [
            ['ely-bf__plan-time', fmtTime(prog.mode === 'up' ? s.at : prog.total - s.at)],
            ['ely-bf__plan-name', s.name],
            ['ely-bf__plan-num', s.unit && s.num !== null && s.num !== undefined ? s.num + s.unit : '']
          ];
          cells.forEach(function (c) {
            var sp = document.createElement('span');
            sp.className = c[0];
            sp.textContent = c[1];
            li.appendChild(sp);
          });
          el.plan.appendChild(li);
        });
      }
      var started = tm.state !== 'idle';
      Array.prototype.forEach.call(el.plan.children, function (li, i) {
        li.className = started && i === idx ? 'is-now' : started && i < idx ? 'is-past' : '';
        if (started && i === idx) li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current');
      });
    }

    // the controls on both faces, and which part of the back face shows
    function renderControls() {
      var guide = guided(), s = tm.state;
      setText(el.go, s === 'paused' ? 'Resume' : guide ? 'Show How to Brew' : 'Start');
      el.over.hidden = s !== 'paused';
      el.frontActions.classList.toggle('has-two', !el.over.hidden);
      setText(el.backGo, s === 'running' ? 'Pause' : s === 'paused' ? 'Resume' : s === 'done' ? 'Brew Again' : guide ? 'Back to Recipe' : 'Pause');
      el.edit.hidden = !(s === 'running' || s === 'paused');
      el.backActions.classList.toggle('has-two', !el.edit.hidden);
      el.guide.hidden = !(guide && s === 'guide');
      el.done.hidden = s !== 'done';
      el.timer.hidden = !el.guide.hidden || !el.done.hidden;
      el.back.setAttribute('data-state', s);
      setText(el.backTitle, s === 'done' ? 'Enjoy' : s === 'guide' ? 'How to Brew' : 'Brew Timer');
    }

    function renderTimer(rec) {
      rec = rec || current();
      renderControls();
      if (rec.m.guide) return null;

      var prog = programme(st, rec);
      var t = elapsed();
      if (prog.mode === 'down' && t >= prog.total) t = prog.total;
      var idx = stepIndex(prog, t);
      var step = prog.steps[idx];
      var nxt = prog.steps[idx + 1];

      setText(el.clock, fmtTime(prog.mode === 'up' ? t : prog.total - t));
      setText(el.nowName, step.name);
      var hasNum = step.num !== null && step.num !== undefined;
      el.nowTarget.hidden = !hasNum;
      if (hasNum) {
        setText(el.nowVerb, step.verb);
        setText(el.nowNum, String(step.num));
        setText(el.nowUnit, step.unit);
      }
      var late = !!(prog.late && t >= prog.late.at);
      setText(el.nowHint, late ? prog.late.hint : step.hint);

      // the next step as a live countdown: "First Pour in 0:12 · to 192 g" (seconds rounded up, so it never reads 0:00 early)
      if (nxt) {
        var left = Math.max(0, Math.ceil(nxt.at - t - 1e-6));
        var to = nxt.say === 'pour to' && nxt.num !== null && nxt.num !== undefined ? ' · to ' + nxt.num + nxt.unit : '';
        setText(el.next, nxt.name + ' in ' + fmtTime(left) + to);
      } else {
        setText(el.next, '');
      }

      // Enjoy: a countdown's last step is still to do (press, pour), so it is named under Enjoy; the pour-over has none left
      var final = prog.steps[prog.steps.length - 1];
      el.last.hidden = prog.mode !== 'down';
      if (prog.mode === 'down') {
        if (el.last.getAttribute('data-for') !== final.name) {
          el.last.innerHTML = '';
          var b = document.createElement('strong');
          b.textContent = final.name + '.';
          el.last.appendChild(b);
          el.last.appendChild(document.createTextNode(' ' + final.hint));
          el.last.setAttribute('data-for', final.name);
        }
      }

      drawTicks(prog);
      drawPlan(prog, idx);
      var frac = Math.max(0, Math.min(1, t / prog.total));
      el.bar.style.transform = 'scaleX(' + frac.toFixed(4) + ')';
      // the clock turns pink at the pour-over's finish time (it keeps counting, as before)
      el.back.classList.toggle('is-done', tm.state === 'done' || late);
      return { prog: prog, idx: idx, t: t };
    }

    function tick() {
      var r = renderTimer();
      if (!r || tm.state !== 'running') return;
      if (r.idx !== tm.idx) {
        tm.idx = r.idx;
        cue();
        say(stepWords(r.prog.steps[r.idx]));
      }
      if (r.prog.late && !tm.late && r.t >= r.prog.late.at) {
        tm.late = true;
        say(r.prog.late.hint);
      }
      if (r.prog.mode === 'down' && r.t >= r.prog.total) finish();
      else if (r.prog.late && r.t >= r.prog.late.at + TASTE_AFTER) finish();
      else if (r.prog.stop && r.t >= r.prog.stop) finish();
    }

    // Start: one tap starts the clock and turns the card over in place
    function start() {
      audioOn(); // inside the tap, so iOS lets it play later
      resetTimer();
      var rec = current();
      tm.state = 'running';
      tm.key = recipeKey(rec);
      tm.since = Date.now();
      tm.idx = 0;
      clearInterval(tm.iv);
      tm.iv = setInterval(tick, 200);
      lockOn();
      tick();
      turn('back', el.backTitle);
      say('Brewing started. ' + stepWords(programme(st, rec).steps[0]));
    }
    function resume() {
      audioOn();
      tm.state = 'running';
      tm.since = Date.now();
      clearInterval(tm.iv);
      tm.iv = setInterval(tick, 200);
      lockOn();
      tick();
    }
    function pause() {
      tm.acc += Date.now() - tm.since;
      tm.state = 'paused';
      clearInterval(tm.iv);
      lockOff();
      renderTimer();
    }
    function finish() {
      var r = renderTimer();
      tm.acc = r.t * 1000;
      tm.state = 'done';
      clearInterval(tm.iv);
      lockOff();
      renderTimer();
      chime();
      if (canBuzz && el.buzzInput && el.buzzInput.checked) { try { navigator.vibrate([120, 80, 120]); } catch (err) {} }
      say('Enjoy. How did it taste?');
    }
    function resetTimer() {
      clearInterval(tm.iv);
      tm.state = 'idle'; tm.acc = 0; tm.since = 0; tm.idx = -1; tm.late = false; tm.key = '';
      lockOff();
      clearTaste();
      if (el.now) el.now.classList.remove('is-step');
      renderControls();
    }

    // the front face's button: Start, Show How to Brew (Auto Brewer), or Resume while a brew is paused
    el.go.addEventListener('click', function () {
      if (tm.state === 'paused') {
        resume();
        turn('back', el.backTitle);
        say('Brewing started.');
      } else if (guided()) {
        tm.state = 'guide';
        renderTimer();
        turn('back', el.backTitle);
        say('How to Brew.');
      } else start();
    });
    // Start Over: the paused brew is dropped, the card stays on the recipe
    el.over.addEventListener('click', function () {
      resetTimer();
      renderTimer();
      focusQuiet(el.go);
      say('Timer reset.');
    });
    // the back face's button: Pause, Resume, Brew Again (after Enjoy), Back to Recipe (How to Brew)
    el.backGo.addEventListener('click', function () {
      if (tm.state === 'running') { pause(); say('Paused at ' + el.clock.textContent + '.'); }
      else if (tm.state === 'paused') { resume(); say('Brewing started.'); }
      else if (tm.state === 'done') {
        resetTimer();
        renderTimer();
        turn('front', el.go);
        say('Recipe.');
      } else {
        tm.state = 'idle';
        renderTimer();
        turn('front', el.go);
        say('Recipe.');
      }
    });
    // Edit Recipe: the brew pauses and the card turns back with every choice kept; Start reads Resume
    el.edit.addEventListener('click', function () {
      if (tm.state === 'running') pause();
      turn('front', el.go);
      say('Recipe. Paused at ' + el.clock.textContent + '.');
    });

    // DRAFT 2026-10-14 (preview only): a preset brewer. First the recipe page's own Brew method (data-ely-recipe-method on the recipe
    // section, round 2), then ?method= in the address, then the section's Brewer setting (data-method). Pour-Over is the markup's
    // default, so with none set nothing changes. Unknown values are ignored.
    var preset = null, page = document.querySelector('[data-ely-recipe-method]');
    if (page) preset = page.getAttribute('data-ely-recipe-method');
    if (!preset || !METHODS[preset]) { try { preset = new window.URLSearchParams(window.location.search).get('method'); } catch (err) {} }
    if (!preset || !METHODS[preset]) preset = root.getAttribute('data-method');
    if (preset && METHODS[preset] && preset !== st.method) {
      el.methods.forEach(function (i) { i.checked = i.value === preset; });
      st.method = preset;
      st.have = METHODS[preset].start.have;
      st.want = METHODS[preset].start.want;
    }

    syncAmountField();
    render(false);
    root.classList.add('is-ready');
  }

  function boot() {
    var roots = document.querySelectorAll('[data-ely-bf]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // theme editor: a re-rendered section gets a fresh calculator
  document.addEventListener('shopify:section:load', boot);
})();
